import React, { useState } from 'react';
import { Tooltip, CircularLoader } from '@dhis2/ui';

function AddObjectiveButton({ 
  onAdd, 
  reportingToObjectives = [], 
  myObjectives = [], 
  goalItems = [], 
  currentUserPosition = null,
  loading = false,
  goalsFullyLoaded = false,
  managerObjectivesLoaded = false,
  protectedObjectives = new Set(),
  childCounts = {},
}) {
  const [hovered, setHovered] = useState(false);
  
  // Wait for all data to finish loading before making decision
  const isLoading = loading || !goalsFullyLoaded || !managerObjectivesLoaded;
  
  // Determine if user is top-level (CEO/Admin)
  let isTopLevelUser = false;
  if (currentUserPosition?.attributes) {
    const reportingToAttr = currentUserPosition.attributes.find(
      attr => attr.attribute === 'SjP47qi3JeB'
    );
    isTopLevelUser = !reportingToAttr?.value;
  }
  
  // Function to check for locked objectives
  const getLockStatus = () => {
    // If not top-level, check for locks
    if (!isTopLevelUser) {
      // Check for protected objectives (have children)
      const protectedObjs = myObjectives.filter(obj => protectedObjectives.has(obj.id));
      if (protectedObjs.length > 0) {
        const details = protectedObjs.map(obj => {
          const count = childCounts[obj.id] || 0;
          return `${obj.name} (${count} aligned objective${count !== 1 ? 's' : ''})`;
        }).join('\n');
        
        return {
          locked: true,
          reason: `You have ${protectedObjs.length} protected objective(s) with linked objectives, you must ask the staff member(s) to unlink or delete the aligned objectives first before you can create new objectives.`,
          type: 'protected'
        };
      }
      
      // Check for approved objectives
      const approvedObjs = myObjectives.filter(obj => obj.approvalStatus === 'approved');
      if (approvedObjs.length > 0) {
        const details = approvedObjs.map(obj => obj.name).join('\n');
        return {
          locked: true,
          reason: `You have ${approvedObjs.length} approved objective(s) that are approved and locked, you must request your manager to return these objectives before creating new ones.`,
          type: 'approved'
        };
      }
    }
    
    // Check if user can create based on goals/manager
    let canCreate = true;
    let createReason = '';
    
    if (isTopLevelUser) {
      // CEO/Admin: Check if ANY goals exist
      if (goalItems.length === 0) {
        canCreate = false;
        createReason = 'No company goals available for the current fiscal year. Please create company goals first.';
      }
    } else {
      // Staff: Can create if manager has objectives OR they already have their own
      const hasManagerObjectives = reportingToObjectives.length > 0;
      const hasUserObjectives = myObjectives.length > 0;
      
      if (!hasManagerObjectives && !hasUserObjectives) {
        canCreate = false;
        createReason = 'Your manager has not created any objectives for this fiscal year. Please wait for your manager to create objectives first.';
      }
    }
    
    return {
      locked: !canCreate,
      reason: createReason,
      type: 'prerequisite'
    };
  };
  
  // Only evaluate if not loading
  let allowed = true;
  let reason = '';
  
  if (!isLoading) {
    const lockStatus = getLockStatus();
    allowed = !lockStatus.locked;
    reason = lockStatus.reason;
  }
  
  // Debug log
  console.log('🔍 [AddObjectiveButton] State:', {
    isLoading,
    allowed,
    reason,
    isTopLevelUser,
    goalsFullyLoaded,
    managerObjectivesLoaded,
    goalItemsCount: goalItems.length,
    reportingToObjectivesCount: reportingToObjectives.length,
    myObjectivesCount: myObjectives.length,
    protectedCount: protectedObjectives.size,
    childCounts: childCounts
  });
  
  const baseStyle = {
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    zIndex: 1000,
  };

  const getButtonColor = () => {
    if (isLoading) return '#9e9e9e';
    return allowed ? '#1c9420' : '#9e9e9e';
  };

  const buttonStyle = {
    borderRadius: hovered ? '14px' : '50%',
    border: 'none',
    width: hovered ? '150px' : '50px',
    height: '50px',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: getButtonColor(),
    color: '#fff',
    cursor: (!isLoading && allowed) ? 'pointer' : 'not-allowed',
    boxShadow: '0 8px 16px rgba(122, 118, 118, 1)',
    transition: 'width 0.3s ease, border-radius 0.3s ease, padding 0.3s ease',
    overflow: 'hidden',
    padding: hovered ? '0 15px' : '0',
    opacity: isLoading ? 0.7 : 1,
  };

  const handleClick = () => {
    if (!isLoading && allowed && onAdd) {
      onAdd();
    } else if (!allowed && reason) {
      // Show alert with the reason
      alert(`❌ Cannot Add Objective\n\n${reason}`);
    }
  };

  const getButtonText = () => {
    if (isLoading) return '⏳';
    if (hovered && allowed) return '+ Add Objective';
    if (hovered && !allowed) return '🔒';
    return '+';
  };

  // Show loading spinner while waiting for data
  if (isLoading) {
    return (
      <div style={baseStyle}>
        <div style={buttonStyle}>
          <CircularLoader small />
        </div>
      </div>
    );
  }

  const buttonElement = (
    <button
      onClick={handleClick}
      style={buttonStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={!allowed}
    >
      {getButtonText()}
    </button>
  );

  if (!allowed) {
    return (
      <div style={baseStyle}>
        <Tooltip content={reason} placement="left">
          {buttonElement}
        </Tooltip>
      </div>
    );
  }

  return <div style={baseStyle}>{buttonElement}</div>;
}

export default AddObjectiveButton;